import React, {useCallback, useMemo, useState} from 'react';
import type {GetCourseVideoListRequest} from 'src/api/ProactApiClient';
import type {
  AutocompleteProps} from 'src/design/atoms/Autocomplete';
import {
  Autocomplete
} from 'src/design/atoms/Autocomplete';
import {useGetCourseVideoSelectOptionsQuery} from 'src/redux/queries/courseVideo';
import type {ISelectOption} from 'src/types/Form';

export interface VideoSelectBoxProps
  extends Omit<AutocompleteProps, 'options' | 'value'> {
  queryOptions?: Partial<GetCourseVideoListRequest>;
  value?: string;
}
export const VideoSelectBox: React.FC<VideoSelectBoxProps> = React.memo(
  ({queryOptions, value, ...other}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');

    const {data, isFetching, isLoading} = useGetCourseVideoSelectOptionsQuery({
      ...queryOptions,
      start: 0,
      length: 1000,
      sortColumn: queryOptions?.sortColumn ?? 'title',
      sortDirection: queryOptions?.sortDirection ?? 'asc',
    });

    const options = useMemo(
      () =>
        ((data?.data ?? []) as ISelectOption[]).filter(i =>
          i.label.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      [data?.data, searchTerm]
    );
    const selectedOption = useMemo(
      () => options?.find(i => String(i.id) === String(value)) ?? null,
      [options, value]
    );

    const handleInputChange = useCallback(
      (event: React.SyntheticEvent, newInputValue: string) => {
        setSearchTerm(newInputValue);
      },
      []
    );

    return (
      <Autocomplete
        {...other}
        disabled={isLoading || other.disabled}
        options={options}
        value={selectedOption}
        onInputChange={handleInputChange}
        loading={isFetching || isLoading}
      />
    );
  }
);
